import React, { useState, useCallback } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  fallbackSrc?: string;
  className?: string;
  style?: React.CSSProperties;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
  onError?: () => void;
  placeholder?: string; // URL for low-res placeholder
  sizes?: string; // Responsive sizes attribute
  priority?: boolean; // For critical images
  fetchpriority?: 'high' | 'low' | 'auto'; // For LCP optimization
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  fallbackSrc,
  className,
  style,
  loading = 'lazy',
  onLoad,
  onError,
  placeholder,
  sizes,
  priority = false,
  fetchpriority = 'auto'
}) => {
  const [currentSrc, setCurrentSrc] = useState(placeholder || src);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(!!placeholder);

  const handleLoad = useCallback(() => {
    if (showPlaceholder && currentSrc === placeholder) {
      // Load the main image after placeholder loads
      setCurrentSrc(src);
      return;
    }
    
    setIsLoading(false);
    setHasError(false);
    setShowPlaceholder(false);
    onLoad?.();
  }, [onLoad, showPlaceholder, currentSrc, placeholder, src]);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
    
    if (fallbackSrc && currentSrc !== fallbackSrc) {
      setCurrentSrc(fallbackSrc);
      setHasError(false);
      setIsLoading(true);
    }
    
    onError?.();
  }, [fallbackSrc, currentSrc, onError]);

  return (
    <div className={`relative ${className || ''}`} style={style}>
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded" />
      )}
      
      <img
        src={currentSrc}
        alt={alt}
        loading={priority ? 'eager' : loading}
        fetchPriority={fetchpriority}
        onLoad={handleLoad}
        onError={handleError}
        sizes={sizes}
        decoding="async"
        className={`transition-all duration-500 ease-in-out ${
          isLoading ? 'opacity-0 scale-105' : 'opacity-100 scale-100'
        } ${showPlaceholder ? 'blur-sm' : 'blur-0'} ${className || ''}`}
        style={{
          ...style,
          display: hasError ? 'none' : 'block',
          contentVisibility: 'auto',
          containIntrinsicSize: '1px 500px'
        }}
      />
      
      {hasError && !fallbackSrc && (
        <div className="flex items-center justify-center bg-gray-100 text-gray-500 text-sm p-4 rounded">
          Falha ao carregar imagem
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;